import React, { useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import PageShell from './PageShell';

import cocoonCaseStudy from '../../images/projects/coccon/case-study.jpg';
import perch from '../../images/projects/graphics/perch.png';
import drizzel from '../../images/projects/drizzel/DRIZZEL.png';
import truerise from '../../images/projects/Truerise/Truerise.png';

// AI PROJECT 03
import ai3Image1 from '../../images/projects/ai-project-3/1.jpg';
import ai3Image2 from '../../images/projects/ai-project-3/2.jpg';
import ai3Image3 from '../../images/projects/ai-project-3/3.jpg';
import ai3Image4 from '../../images/projects/ai-project-3/4.jpg';
import ai3Image5 from '../../images/projects/ai-project-3/5.jpg';
import ai3Image6 from '../../images/projects/ai-project-3/6.jpg';
import ai3Image7 from '../../images/projects/ai-project-3/7.jpg';
import ai3Image8 from '../../images/projects/ai-project-3/8.png';
import ai3Image9 from '../../images/projects/ai-project-3/9.png';

const categoryLabel = {
  'graphic-design': 'Graphic Design',
  'ui-ux': 'UI / UX Design',
  'ai-creative': 'AI Creative',
};

const aiProject3Images = [
  ai3Image1,
  ai3Image2,
  ai3Image3,
  ai3Image4,
  ai3Image5,
  ai3Image6,
  ai3Image7,
  ai3Image8,
  ai3Image9,
];

const ProjectPage = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const category = location.pathname.split('/').filter(Boolean)[0];
  const navigate = useNavigate();

  const label = categoryLabel[category] || 'Project';
  const num = projectId?.replace('project-', '') || '';

  const isCocoon = category === 'ui-ux' && num === '1';
  const isPerch = category === 'graphic-design' && num === '1';
  const isDrizzel = category === 'graphic-design' && num === '2';
  const isTruerise = category === 'ui-ux' && num === '2';

  // AI CREATIVE PROJECTS
  const isMastercard = category === 'ai-creative' && num === '1';
  const isDotAndKey = category === 'ai-creative' && num === '2';
  const isAIProject3 = category === 'ai-creative' && num === '3';

  // CAROUSEL STATE
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const nextSlide = () => {
    setCurrentSlide((prev) =>
      prev === aiProject3Images.length - 1 ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? aiProject3Images.length - 1 : prev - 1
    );
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;

    if (Math.abs(distance) > 50) {
      if (distance > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const projectTitle = isCocoon
    ? 'Project Cocoon'
    : isPerch
      ? 'Project Perch'
      : isDrizzel
        ? 'Project Drizzel'
        : isTruerise
          ? 'Project Truerise'
          : isMastercard
            ? 'Project Mastercard'
            : isDotAndKey
              ? 'Project Dot & Key'
              : isAIProject3
                ? 'AI Campaign'
                : `Project ${num}`;

  const description = isCocoon
    ? 'Cocoon is a UX case study focused on redesigning the digital experience of a nonprofit organization dedicated to empowering underserved communities. The project prioritizes accessibility, trust, and intuitive navigation, enabling volunteers, donors, and beneficiaries to connect more effectively. Through a user-centered design approach, the platform creates a seamless, engaging, and meaningful experience while strengthening the connection between people and the communities they support.'
    : isPerch
      ? 'Perch is a thoughtfully designed bird nutrition brand created to make everyday bird care feel healthier, more joyful, and more trustworthy. The brand identity combines a warm, nature-inspired visual language with playful yet premium packaging designed to appeal to modern bird owners. From the logo and colour palette to the packaging and supporting visual assets, every element works together to communicate natural nutrition, care, and happiness.'
      : isDrizzel
        ? 'Drizzel is a bold, flavour-forward sauce brand built around playful energy, expressive packaging, and a strong visual identity. The project explores a vibrant brand system across multiple sauce flavours, combining distinctive typography, rich colours, and appetising product visuals to create a memorable and contemporary food brand.'
        : isTruerise
          ? 'True Rise is a one-to-one mentorship platform designed to help students navigate academic and career decisions with greater clarity and confidence. Instead of generic counselling or overwhelming online advice, the platform connects students with experienced mentors for personal, honest conversations grounded in real-world journeys. True Rise aims to replace confusion with clarity and help students make informed decisions based on their individual goals, challenges, and aspirations.'
          : isMastercard
            ? 'An AI-powered creative exploration created for Mastercard, using generative visuals and motion to transform a familiar brand language into an experimental visual experience.'
            : isDotAndKey
              ? 'An AI-powered visual exploration created for Dot & Key, using generative imagery and motion to create an expressive and visually engaging brand experience.'
              : isAIProject3
                ? 'A collection of AI-driven advertising explorations created across beauty, technology, and lifestyle brands. The project explores how generative AI can be used to develop cinematic product visuals, storytelling, and campaign concepts while maintaining a strong brand-focused visual direction.'
                : 'Project overview coming soon.';

  const projectImage = isCocoon
    ? cocoonCaseStudy
    : isPerch
      ? perch
      : isDrizzel
        ? drizzel
        : isTruerise
          ? truerise
          : '';

  const projectAlt = isCocoon
    ? 'Cocoon Case Study'
    : isPerch
      ? 'Perch'
      : isDrizzel
        ? 'Drizzel'
        : isTruerise
          ? 'Truerise'
          : isMastercard
            ? 'Mastercard AI Creative'
            : isDotAndKey
              ? 'Dot & Key AI Creative'
              : isAIProject3
                ? `AI Creative Project 03 Slide ${currentSlide + 1}`
                : `Project ${num}`;

  return (
    <PageShell>

      {/* PROJECT HEADER */}
      <div className="border-b-2 divider-line pb-8 sm:pb-10 mb-10 sm:mb-12">

        <button
          onClick={() => {
            navigate(`/${category}`);
            window.scrollTo({
              top: 0,
              behavior: 'auto',
            });
          }}
          className="inline-flex items-center gap-2 text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.28em] font-sans-ui text-[#1a1a1a] link-underline"
        >
          <ArrowLeft size={12} />
          BACK TO {label.toUpperCase()}
        </button>

        <p className="text-[10px] sm:text-[11px] tracking-[0.25em] sm:tracking-[0.28em] font-sans-ui text-[#8b2331] mt-7 sm:mt-8">
          {label.toUpperCase()} &middot; PROJECT NO. 0{num}
        </p>

        <h1 className="font-serif-display text-[48px] sm:text-[60px] md:text-[72px] leading-[0.98] mt-3 text-[#0f0f0f]">
          {projectTitle}
        </h1>

        {/* DESCRIPTION + DETAILS */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-6">

          {/* DESCRIPTION */}
          <div className="md:col-span-8">
            <p className="font-serif-body text-[17px] sm:text-[18px] leading-relaxed text-[#2a2a2a] italic">
              {description}
            </p>
          </div>

          {/* PROJECT DETAILS */}
          <div className="md:col-span-4 border-t md:border-t-0 md:border-l divider-line pt-5 md:pt-0 md:pl-6">

            <div>
              <p className="text-[9px] sm:text-[10px] tracking-[0.28em] font-sans-ui text-[#0f0f0f]">
                CATEGORY
              </p>

              <p className="font-serif-body text-[16px] text-[#2a2a2a] mt-1">
                {label}
              </p>
            </div>

            <div className="mt-4">
              <p className="text-[9px] sm:text-[10px] tracking-[0.28em] font-sans-ui text-[#0f0f0f]">
                YEAR
              </p>

              <p className="font-serif-body text-[16px] text-[#2a2a2a] mt-1">
                2026
              </p>
            </div>

            <div className="mt-4">
              <p className="text-[9px] sm:text-[10px] tracking-[0.28em] font-sans-ui text-[#0f0f0f]">
                ROLE
              </p>

              <p className="font-serif-body text-[16px] text-[#2a2a2a] mt-1">
                Designer
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* PROJECT MEDIA */}
      <div className="space-y-8 sm:space-y-10">

        {/* MASTERCARD VIDEO */}
        {isMastercard ? (
          <video
            src="/videos/mastercard/mastercard.mp4"
            controls
            playsInline
            preload="metadata"
            className="w-full md:w-[30%] h-auto block mx-auto"
          />

        /* DOT & KEY VIDEO */
        ) : isDotAndKey ? (
          <video
            src="/videos/dotandkey/dotandkey.mp4"
            controls
            playsInline
            preload="metadata"
            className="w-full md:w-[30%] h-auto block mx-auto"
          />

        /* AI PROJECT 03 CAROUSEL */
        ) : isAIProject3 ? (
          <div className="w-full">

            <div
              className="relative w-full max-w-[900px] mx-auto overflow-hidden touch-pan-y"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >

              {/* SLIDES */}
              <div
                className="flex w-full transition-transform duration-300 ease-out"
                style={{
                  transform: `translateX(-${currentSlide * 100}%)`,
                }}
              >

                {aiProject3Images.map((image, index) => (
                  <div
                    key={index}
                    className="w-full min-w-full flex-shrink-0 flex justify-center"
                  >

                    {/* MOBILE: FULL WIDTH / DESKTOP: CONTROLLED WIDTH */}
                    <div className="w-full sm:w-auto sm:max-w-[40vw] mx-auto">
                      <img
                        src={image}
                        alt={`AI Creative Project 03 Slide ${index + 1}`}
                        className="w-full h-auto block"
                        draggable="false"
                      />
                    </div>

                  </div>
                ))}

              </div>

              {/* LEFT ARROW — DESKTOP ONLY */}
              <button
                onClick={prevSlide}
                aria-label="Previous slide"
                className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-[#1a1a1a] text-[#f0ebe0] items-center justify-center hover:bg-[#8b2331] transition-colors"
              >
                <ChevronLeft size={22} />
              </button>

              {/* RIGHT ARROW — DESKTOP ONLY */}
              <button
                onClick={nextSlide}
                aria-label="Next slide"
                className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-[#1a1a1a] text-[#f0ebe0] items-center justify-center hover:bg-[#8b2331] transition-colors"
              >
                <ChevronRight size={22} />
              </button>

            </div>

            {/* SLIDE COUNTER */}
            <div className="flex items-center justify-center mt-4">
              <p className="text-[10px] tracking-[0.28em] font-sans-ui text-[#8b2331]">
                {currentSlide + 1} / {aiProject3Images.length}
              </p>
            </div>

          </div>

        /* EXISTING IMAGE PROJECTS */
        ) : projectImage ? (
          <img
            src={projectImage}
            alt={projectAlt}
            className="project-image"
          />

        /* EMPTY PROJECT */
        ) : (
          <div className="w-full min-h-[300px] border border-[#1a1a1a] bg-[#ece5d3] flex items-center justify-center">
            <span className="font-serif-display text-[28px] sm:text-[42px] text-[#3a3a3a]">
              PROJECT {num}
            </span>
          </div>
        )}

      </div>

      {/* PROJECT FOOTER */}
      <div className="mt-12 sm:mt-16 border-t-2 divider-line pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">

        <button
          onClick={() => {
            navigate(`/${category}`);
            window.scrollTo({
              top: 0,
              behavior: 'auto',
            });
          }}
          className="inline-flex items-center gap-2 text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.28em] font-sans-ui text-[#1a1a1a] link-underline"
        >
          <ArrowLeft size={12} />
          ALL {label.toUpperCase()} PROJECTS
        </button>

        <p className="text-[9px] sm:text-[10px] tracking-[0.28em] font-sans-ui text-[#8b2331] sm:text-right">
          PROJECT NO. 0{num}
        </p>

      </div>

    </PageShell>
  );
};

export default ProjectPage;